import { logger } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import {
  GET_PATIENT_DOCUMENTS,
  type PatientDocumentResponse,
  type QueryPatientDocument,
} from '../../contracts';
import { uploadPatientDocument } from '../api/patients';

/**
 * Single-site mutation identity. It stays INLINE rather than moving to
 * `/contracts` because it is not shared across a split pair — the same reason
 * `'DeletePatientProfile'` is deliberately not a contracts key (see
 * `contracts/keys.ts`). The app delegate re-exports the name so every existing
 * importer keeps resolving at the old path.
 */
export const UPLOAD_PATIENT_DOCUMENTS = 'upload_patient_documents';

/**
 * Map of in-flight uploads so the card can cancel a single upload before it
 * completes. Keyed by `${patientProfileId}::${fileName}`.
 */
const activeUploadControllers = new Map<string, AbortController>();

const uploadKey = (patientProfileId: string, fileName: string) =>
  `${patientProfileId}::${fileName}`;

export interface UseMutateUploadPatientDocumentsPorts {
  /**
   * Fires where the legacy body's analytics + success toast fired: inside
   * `onSuccess`, AFTER the `count === 0` early return, so a zero-result upload
   * still emits nothing. `results` preserves input order (Promise.all over
   * files.map), so the delegate can line each result up with its source File
   * for size/type — that pairing is why the port carries both.
   */
  onUploaded?: (results: PatientDocumentResponse[], files: File[]) => void;
  /** Fires where the legacy failure toast fired, after the log. */
  onUploadError?: (error: Error) => void;
}

/**
 * Upload one or more files to a patient profile (carved ARCFE-1054).
 *
 * `POST /patient-profiles/{id}/documents` is patients' own resource and its api
 * fn already moved at D1.1, so this hook had no cross-domain obligation to
 * resolve — unlike its session↔document-link neighbours, which write a
 * `@domain session` route and stayed put pending an ownership ruling.
 *
 * Every cache write here targets `GET_PATIENT_DOCUMENTS`, this package's own
 * identity: the optimistic prepend, the per-file progress writes and the
 * settle-time invalidation are all in-domain and stay in the package. Only the
 * analytics and the toasts leave, through the ports above.
 *
 * Pattern mirrors `useMutateUploadContextDocuments` — per-file AbortController,
 * progress tracked into the query cache via `loadingProgress`, optimistic
 * prepend with `temp_*` ids.
 */
export function useMutateUploadPatientDocuments(
  patientProfileId: string,
  { onUploaded, onUploadError }: UseMutateUploadPatientDocumentsPorts = {}
) {
  const queryClient = useQueryClient();

  const onProgressUpdate = (fileName: string, progress: number) => {
    queryClient.setQueryData<QueryPatientDocument[]>(
      [GET_PATIENT_DOCUMENTS, { patientProfileId }],
      (oldData) =>
        oldData?.map((d) =>
          d.file_name === fileName ? { ...d, loadingProgress: progress } : d
        )
    );
  };

  const mutation = useMutation({
    mutationKey: [UPLOAD_PATIENT_DOCUMENTS, { patientProfileId }],
    mutationFn: async ({
      files,
      description,
      sessionId,
    }: {
      files: File[];
      description?: string | null;
      sessionId?: string | null;
    }) => {
      const results = await Promise.all(
        files.map((file) => {
          const controller = new AbortController();
          activeUploadControllers.set(uploadKey(patientProfileId, file.name), controller);

          return uploadPatientDocument({
            patientProfileId,
            file,
            description,
            sessionId,
            signal: controller.signal,
            onProgressUpdate,
          }).finally(() => {
            activeUploadControllers.delete(uploadKey(patientProfileId, file.name));
          });
        })
      );
      return results.map((r) => r.data as PatientDocumentResponse);
    },
    onMutate: ({ files }) => {
      // Optimistic prepend so the UI shows the in-flight files immediately.
      queryClient.setQueryData<QueryPatientDocument[]>(
        [GET_PATIENT_DOCUMENTS, { patientProfileId }],
        (oldData) => {
          const pending: QueryPatientDocument[] = files.map((file) => ({
            id: `temp_${file.name}_${Date.now()}`,
            patient_profile_id: patientProfileId,
            uploaded_by: '',
            // No uploader identity on an optimistic row; the predicate fails
            // closed on it, and the card bypasses the predicate for `temp_*`
            // rows anyway (cancel, not delete).
            uploader_user_ref_id: null,
            file_name: file.name,
            mime_type: file.type,
            size_bytes: file.size,
            description: null,
            session_id: null,
            summary: null,
            // Optimistic in-flight entry: shown via loadingProgress, not the
            // BE-1c pipeline state. `is_ready: true` keeps it out of the
            // "not available yet" greyed treatment (the card also overrides
            // status to ready for temp/uploading rows).
            is_ready: true,
            failure_reason: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            loadingProgress: 0,
          }));
          return produce(oldData ?? [], (draft) => {
            draft.unshift(...pending);
          });
        }
      );
    },
    onSuccess: (results, { files }) => {
      const count = results.length;
      if (count === 0) return;
      onUploaded?.(results, files);
    },
    onError: (error: Error) => {
      logger.error(
        `Error uploading patient documents for ${patientProfileId}: ${error.message}`
      );
      onUploadError?.(error);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [GET_PATIENT_DOCUMENTS, { patientProfileId }],
      });
    },
  });

  return {
    ...mutation,
    /**
     * Abort a single in-flight upload by filename. Returns `true` if an
     * upload was found and cancelled.
     */
    cancelUpload: (fileName: string) => {
      const controller = activeUploadControllers.get(
        uploadKey(patientProfileId, fileName)
      );
      if (!controller) return false;
      controller.abort();
      activeUploadControllers.delete(uploadKey(patientProfileId, fileName));
      return true;
    },
  };
}
